/**
 * Payment Processing Page
 * Handle customer payments, refunds, and transaction management
 * Stax integration for secure payment processing
 */

import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import {
  CreditCard,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  Download,
  Filter,
  Search,
  Plus
} from 'lucide-react';

interface PaymentTransaction {
  id: string;
  customer_id: string;
  customer_name: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  payment_method: string;
  transaction_id: string;
  created_at: string;
  description: string;
  metadata?: any;
}

interface PaymentFormData {
  customer_id: string;
  amount: number;
  description: string;
  payment_method: 'card' | 'bank' | 'wallet';
}

export default function PaymentProcessingPage() {
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [formData, setFormData] = useState<PaymentFormData>({
    customer_id: '',
    amount: 0,
    description: '',
    payment_method: 'card'
  });

  useEffect(() => {
    loadTransactions();
    loadPaymentStats();
  }, [statusFilter]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      // Mock data - in production would fetch from payment_transactions table
      const mockTransactions: PaymentTransaction[] = [
        {
          id: '1',
          customer_id: 'cust_001',
          customer_name: 'John Smith',
          amount: 29900,
          currency: 'USD',
          status: 'completed',
          payment_method: 'card',
          transaction_id: 'stx_1234567890',
          created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
          description: 'Monthly warranty payment'
        },
        {
          id: '2',
          customer_id: 'cust_002',
          customer_name: 'Sarah Johnson',
          amount: 19900,
          currency: 'USD',
          status: 'pending',
          payment_method: 'bank',
          transaction_id: 'stx_1234567891',
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
          description: 'Setup fee'
        },
        {
          id: '3',
          customer_id: 'cust_003',
          customer_name: 'Mike Davis',
          amount: 45000,
          currency: 'USD',
          status: 'failed',
          payment_method: 'card',
          transaction_id: 'stx_1234567892',
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(), // 4 hours ago
          description: 'Annual premium plan'
        }
      ];

      let filtered = mockTransactions;
      if (statusFilter !== 'all') {
        filtered = mockTransactions.filter(t => t.status === statusFilter);
      }
      if (searchTerm) {
        filtered = filtered.filter(t =>
          t.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.transaction_id.includes(searchTerm)
        );
      }

      setTransactions(filtered);
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
    setLoading(false);
  };

  const loadPaymentStats = async () => {
    // Mock stats - in production would calculate from real data
    const stats = {
      totalRevenue: 94700,
      pendingPayments: 19900,
      failedPayments: 45000,
      successRate: 94.2
    };
    // Update stats cards with real data
  };

  const processPayment = async () => {
    if (!formData.customer_id || !formData.amount || !formData.description) {
      alert('Please fill in all required fields');
      return;
    }

    setProcessing(true);
    try {
      // Mock Stax API integration
      const response = await mockStaxPayment(formData);

      if (response.success) {
        const newTransaction: PaymentTransaction = {
          id: Date.now().toString(),
          customer_id: formData.customer_id,
          customer_name: 'New Customer', // Would lookup from customer data
          amount: formData.amount * 100, // Convert to cents
          currency: 'USD',
          status: 'completed',
          payment_method: formData.payment_method,
          transaction_id: response.transactionId || '',
          created_at: new Date().toISOString(),
          description: formData.description
        };

        setTransactions(prev => [newTransaction, ...prev]);
        setShowPaymentForm(false);
        setFormData({ customer_id: '', amount: 0, description: '', payment_method: 'card' });
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      alert('Payment failed: ' + (error as Error).message);
    }
    setProcessing(false);
  };

  const mockStaxPayment = async (paymentData: PaymentFormData) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Mock success/failure
    if (Math.random() > 0.1) { // 90% success rate
      return {
        success: true,
        transactionId: 'stx_' + Date.now(),
        status: 'completed'
      };
    } else {
      return {
        success: false,
        error: 'Card declined by issuer'
      };
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: 'bg-green-500/10 text-green-400 border-green-500/20',
      pending: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      failed: 'bg-red-500/10 text-red-400 border-red-500/20',
      refunded: 'bg-blue-500/10 text-blue-400 border-blue-500/20'
    };

    return variants[status as keyof typeof variants] || variants.pending;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle size={16} className="text-green-400" />;
      case 'pending': return <Clock size={16} className="text-yellow-400" />;
      case 'failed': return <AlertCircle size={16} className="text-red-400" />;
      case 'refunded': return <RefreshCw size={16} className="text-blue-400" />;
      default: return <Clock size={16} className="text-gray-400" />;
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminPageLayout
      title="Payment Processing"
      subtitle="Secure payment processing with Stax integration"
      actions={
        <div className="flex gap-2">
          <Button
            onClick={() => setShowPaymentForm(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus size={16} className="mr-2" />
            Process Payment
          </Button>
          <Button variant="outline">
            <Download size={16} className="mr-2" />
            Export
          </Button>
        </div>
      }
    >
      {/* Payment Statistics */}
      <StatsGrid cols={4}>
        <StatCard
          label="Total Revenue"
          value="$947.00"
          icon={DollarSign}
          subtitle="↑ 12% from last month"
        />
        <StatCard
          label="Pending Payments"
          value="$199.00"
          icon={Clock}
          subtitle="3 transactions"
        />
        <StatCard
          label="Failed Payments"
          value="$45.00"
          icon={AlertCircle}
          subtitle="1 transaction"
        />
        <StatCard
          label="Success Rate"
          value="94.2%"
          icon={TrendingUp}
          subtitle="↑ 2.1% from last week"
        />
      </StatsGrid>

      {/* Filters and Search */}
      <Card className="p-4 mb-6">
        <div className="flex gap-4 items-center">
          <div className="flex-1">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by customer or transaction ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={(value: string) => setStatusFilter(value)}>
            <SelectTrigger className="w-48">
              <Filter size={16} className="mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <Card className="p-6 mb-6 border-blue-500/20 bg-blue-500/5">
          <h3 className="text-lg font-semibold mb-4">Process New Payment</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor="customer_id">Customer ID</Label>
              <Input
                id="customer_id"
                value={formData.customer_id}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_id: e.target.value }))}
                placeholder="Enter customer ID"
              />
            </div>
            <div>
              <Label htmlFor="amount">Amount (USD)</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="payment_method">Payment Method</Label>
              <Select value={formData.payment_method} onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method: value as 'card' | 'bank' | 'wallet' }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="card">Credit Card</SelectItem>
                  <SelectItem value="bank">Bank Account</SelectItem>
                  <SelectItem value="wallet">Digital Wallet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Payment description"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={processPayment}
              disabled={processing}
              className="bg-green-600 hover:bg-green-700"
            >
              {processing ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard size={16} className="mr-2" />
                  Process Payment
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowPaymentForm(false)}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {/* Transactions Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Transactions</h3>
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-slate-800/50 animate-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between p-4 border border-slate-700 rounded-lg hover:bg-slate-800/50 transition-colors">
                <div className="flex items-center gap-4">
                  {getStatusIcon(transaction.status)}
                  <div>
                    <div className="font-medium text-white">{transaction.customer_name}</div>
                    <div className="text-sm text-slate-400">{transaction.description}</div>
                    <div className="text-xs text-slate-500">{transaction.transaction_id}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-white">{formatAmount(transaction.amount)}</div>
                  <div className="text-sm text-slate-400">{formatDate(transaction.created_at)}</div>
                </div>
                <Badge className={getStatusBadge(transaction.status)}>
                  {transaction.status.toUpperCase()}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </AdminPageLayout>
  );
}
